import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, ClockIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import PageHeader from '../../components/common/PageHeader';
import { careerDirectionApi, errorMessage } from './api';

const TABS = [
    { key: 'interns', label: 'Interns' },
    { key: 'attention', label: 'Needs attention' },
    { key: 'upload', label: 'Bulk upload' },
    { key: 'reference', label: 'Reference list' },
    { key: 'settings', label: 'Settings' }
];

const inputClass = 'w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-ink-900 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20';
const SOURCE_LABEL = { auto: 'from degree', admin: 'set by admin', csv: 'CSV upload' };

function formatDate(value) {
    if (!value) return '';
    try {
        return new Date(value).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch {
        return '';
    }
}

function downloadBlob(blob, name) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function AdminCareerDirections() {
    const [tab, setTab] = useState('interns');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({ degree_group: '', job_title: '', search: '' });

    const load = useCallback(() => {
        careerDirectionApi.interns({
            degree_group: filters.degree_group || undefined,
            job_title: filters.job_title || undefined,
            search: filters.search || undefined,
            needs_attention: tab === 'attention' ? 'true' : undefined
        })
            .then((d) => { setData(d); setError(null); })
            .catch((err) => setError(errorMessage(err, 'Could not load career directions')));
    }, [filters, tab]);

    useEffect(() => {
        if (tab !== 'interns' && tab !== 'attention') return undefined;
        const id = setTimeout(load, filters.search ? 250 : 0);
        return () => clearTimeout(id);
    }, [load, tab, filters.search]);

    const groups = useMemo(() => (data ? data.groups : []), [data]);

    return (
        <div className="mx-auto w-full max-w-7xl space-y-6 pb-12">
            <PageHeader
                eyebrow="People"
                title="Career directions"
                description="Each intern on the 3-month program gets a career direction from their degree. These are role directions, not guaranteed job titles, and never affect assessment, completion or progression."
            />

            {data && (
                <div className="grid grid-cols-3 gap-4">
                    <Stat label="Interns" value={data.counts.total} />
                    <Stat label="Needs attention" value={data.counts.needs_attention} warn={data.counts.needs_attention > 0} onClick={() => setTab('attention')} />
                    <Stat label="Admin overrides" value={data.counts.overridden} />
                </div>
            )}

            <div role="tablist" className="flex gap-1 overflow-x-auto border-b border-border">
                {TABS.map((t) => (
                    <button
                        key={t.key} role="tab" type="button" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
                        className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium ${tab === t.key ? 'border-brand-700 text-brand-800' : 'border-transparent text-ink-600 hover:text-ink-900'}`}
                    >
                        {t.label}
                        {t.key === 'attention' && data && data.counts.needs_attention > 0 && (
                            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">{data.counts.needs_attention}</span>
                        )}
                    </button>
                ))}
            </div>

            {error && <p className="text-sm text-red-700">{error}</p>}

            {(tab === 'interns' || tab === 'attention') && (
                <InternsTab
                    data={data}
                    groups={groups}
                    filters={filters}
                    setFilters={setFilters}
                    attention={tab === 'attention'}
                    reload={load}
                />
            )}
            {tab === 'upload' && <UploadTab onApplied={load} />}
            {tab === 'reference' && <ReferenceTab />}
            {tab === 'settings' && <SettingsTab />}
        </div>
    );
}

function Stat({ label, value, warn, onClick }) {
    const Tag = onClick ? 'button' : 'div';
    return (
        <Tag type={onClick ? 'button' : undefined} onClick={onClick} className={`rounded-2xl border bg-white px-5 py-4 text-left ${warn ? 'border-amber-300' : 'border-border'} ${onClick ? 'hover:border-ink-300' : ''}`}>
            <p className="text-xs text-ink-500">{label}</p>
            <p className={`mt-1 text-2xl font-bold ${warn ? 'text-amber-700' : 'text-ink-950'}`}>{value}</p>
        </Tag>
    );
}

function InternsTab({ data, groups, filters, setFilters, attention, reload }) {
    const [openHistory, setOpenHistory] = useState(null);
    const [busy, setBusy] = useState(null);
    const allTitles = useMemo(() => [...new Set(groups.flatMap((g) => [g.primary_title, g.alternate_1, g.alternate_2].filter(Boolean)))].sort(), [groups]);
    const byCode = useMemo(() => Object.fromEntries(groups.map((g) => [g.code, g])), [groups]);

    const run = async (key, action, success) => {
        setBusy(key);
        try {
            await action();
            toast.success(success);
            reload();
        } catch (err) {
            toast.error(errorMessage(err, 'Could not save the change'));
        } finally {
            setBusy(null);
        }
    };

    const exportCsv = async () => {
        try {
            const blob = await careerDirectionApi.exportCsv({
                degree_group: filters.degree_group || undefined,
                job_title: filters.job_title || undefined,
                search: filters.search || undefined,
                needs_attention: attention ? 'true' : undefined
            });
            downloadBlob(blob, `career-directions-${new Date().toISOString().slice(0, 10)}.csv`);
        } catch (err) {
            toast.error(errorMessage(err, 'Could not export'));
        }
    };

    return (
        <div className="space-y-4">
            {attention && (
                <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>These interns have no degree on their profile, so no career direction could be chosen. Set their degree group here, upload a CSV, or ask them to add their degree to their Talent Profile.</p>
                </div>
            )}

            <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
                {!attention && (
                    <>
                        <select aria-label="Filter by degree group" className={`${inputClass} md:w-64`} value={filters.degree_group} onChange={(e) => setFilters((f) => ({ ...f, degree_group: e.target.value }))}>
                            <option value="">All degree groups</option>
                            {groups.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
                        </select>
                        <select aria-label="Filter by career direction" className={`${inputClass} md:w-64`} value={filters.job_title} onChange={(e) => setFilters((f) => ({ ...f, job_title: e.target.value }))}>
                            <option value="">All career directions</option>
                            {allTitles.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </>
                )}
                <input
                    type="search" aria-label="Search interns" placeholder="Search name, email, student ID or degree…"
                    className={`${inputClass} min-w-0 flex-1 md:min-w-[16rem]`} value={filters.search}
                    onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                />
                <button type="button" onClick={exportCsv} className="btn-secondary inline-flex items-center gap-1.5 whitespace-nowrap">
                    <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
                </button>
            </div>

            <div className="overflow-hidden rounded-2xl border border-border bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[980px] text-left text-sm">
                        <thead className="border-b border-border text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                            <tr>
                                <th className="px-4 py-3">Intern</th>
                                <th className="px-4 py-3">Degree (from profile)</th>
                                <th className="px-4 py-3">Degree group</th>
                                <th className="px-4 py-3">Career direction</th>
                                <th className="px-4 py-3"><span className="sr-only">History</span></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {!data && <tr><td colSpan={5} className="px-4 py-4"><div className="h-5 animate-pulse rounded bg-ink-100" /></td></tr>}
                            {data && data.rows.length === 0 && (
                                <tr><td colSpan={5} className="px-4 py-10 text-center text-ink-500">{attention ? 'Every intern has a degree group. Nothing needs attention.' : 'No interns match these filters.'}</td></tr>
                            )}
                            {data && data.rows.map((row) => {
                                const group = byCode[row.degree_group];
                                const groupTitles = group ? [group.primary_title, group.alternate_1, group.alternate_2].filter(Boolean) : [];
                                const otherTitles = allTitles.filter((t) => !groupTitles.includes(t));
                                const key = row.profile_id;
                                return (
                                    <React.Fragment key={key}>
                                        <tr className="align-top">
                                            <td className="px-4 py-3">
                                                <p className="font-semibold text-ink-950">{row.name}</p>
                                                <p className="text-xs text-ink-500">{row.email}</p>
                                                {row.student_id && <p className="text-xs text-ink-400">ID {row.student_id}</p>}
                                            </td>
                                            <td className="px-4 py-3 text-ink-700">{row.degree || <span className="italic text-ink-400">Not provided</span>}</td>
                                            <td className="px-4 py-3">
                                                <select
                                                    aria-label={`Degree group for ${row.name}`} disabled={busy === key}
                                                    className={inputClass} value={row.degree_group || ''}
                                                    onChange={(e) => e.target.value && run(key, () => careerDirectionApi.setGroup(key, e.target.value), 'Degree group updated — title reset to its primary')}
                                                >
                                                    {!row.degree_group && <option value="">— Not set —</option>}
                                                    {groups.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
                                                </select>
                                                {row.degree_group_source && (
                                                    <p className="mt-1 text-[11px] text-ink-500">
                                                        {SOURCE_LABEL[row.degree_group_source] || row.degree_group_source}
                                                        {row.degree_group_source !== 'auto' && row.degree && (
                                                            <> · <button type="button" className="text-brand-700 hover:underline" onClick={() => run(key, () => careerDirectionApi.autoGroup(key), 'Now following the degree on their profile')}>match from degree</button></>
                                                        )}
                                                    </p>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                {row.degree_group ? (
                                                    <>
                                                        <select
                                                            aria-label={`Career direction for ${row.name}`} disabled={busy === key}
                                                            className={inputClass} value={row.job_title || ''}
                                                            onChange={(e) => run(key, () => careerDirectionApi.setTitle(key, e.target.value), 'Career direction overridden')}
                                                        >
                                                            <optgroup label={group ? group.name : 'This group'}>
                                                                {groupTitles.map((t, i) => <option key={t} value={t}>{t}{i === 0 ? ' (primary)' : ''}</option>)}
                                                            </optgroup>
                                                            <optgroup label="Other titles in the reference list">
                                                                {otherTitles.map((t) => <option key={t} value={t}>{t}</option>)}
                                                            </optgroup>
                                                        </select>
                                                        <p className="mt-1 text-[11px] text-ink-500">
                                                            {row.job_title_source === 'admin' ? (
                                                                <>
                                                                    <span className="font-semibold text-amber-700">Admin override</span> ·{' '}
                                                                    <button type="button" className="text-brand-700 hover:underline" onClick={() => run(key, () => careerDirectionApi.resetTitle(key), 'Back to the primary title')}>use primary</button>
                                                                </>
                                                            ) : 'Primary title for this group'}
                                                        </p>
                                                    </>
                                                ) : <span className="text-xs italic text-ink-400">Set a degree group first</span>}
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    type="button" aria-expanded={openHistory === key}
                                                    onClick={() => setOpenHistory(openHistory === key ? null : key)}
                                                    className="inline-flex items-center gap-1 text-xs font-medium text-ink-600 hover:text-brand-700"
                                                >
                                                    <ClockIcon className="h-4 w-4" /> History ({row.history.length})
                                                </button>
                                            </td>
                                        </tr>
                                        {openHistory === key && (
                                            <tr>
                                                <td colSpan={5} className="bg-ink-50/60 px-4 py-3">
                                                    {row.history.length === 0 ? <p className="text-xs text-ink-500">No changes yet.</p> : (
                                                        <ol className="space-y-1.5">
                                                            {[...row.history].reverse().map((h, i) => (
                                                                <li key={i} className="text-xs text-ink-700">
                                                                    <span className="text-ink-500">{formatDate(h.at)}</span> · <span className="font-medium">{h.by_name}</span> · {h.reason}:{' '}
                                                                    {h.from_title ? <><span className="line-through">{h.from_title}</span> → </> : null}
                                                                    <span className="font-semibold">{h.to_title}</span>
                                                                    {h.from_group !== h.to_group && h.to_group ? <span className="text-ink-500"> (group {h.from_group || 'none'} → {h.to_group})</span> : null}
                                                                </li>
                                                            ))}
                                                        </ol>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const TEMPLATE = 'student_id,degree_group_code\r\nS1234567,finance-economics\r\nintern@example.com,cs-software\r\n';

function UploadTab({ onApplied }) {
    const [csv, setCsv] = useState('');
    const [fileName, setFileName] = useState('');
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(null);

    const readFile = (file) => {
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error('File is too large (max 2 MB)');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => { setCsv(String(reader.result || '')); setFileName(file.name); setPreview(null); };
        reader.readAsText(file);
    };

    const runPreview = async () => {
        setBusy('preview');
        try {
            setPreview(await careerDirectionApi.previewCsv(csv));
        } catch (err) {
            toast.error(errorMessage(err, 'Could not read the file'));
        } finally {
            setBusy(null);
        }
    };

    const commit = async () => {
        setBusy('commit');
        try {
            const result = await careerDirectionApi.commitCsv(csv);
            toast.success(`Applied ${result.applied} row${result.applied === 1 ? '' : 's'}${result.failed.length ? ` · ${result.failed.length} skipped` : ''}`);
            setPreview({ ...preview, committed: result });
            onApplied();
        } catch (err) {
            toast.error(errorMessage(err, 'Could not apply the file'));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-4">
            <section className="rounded-2xl border border-border bg-white p-5">
                <h2 className="text-[15px] font-semibold text-ink-950">Backfill existing interns from a CSV</h2>
                <p className="mt-1 text-sm text-ink-600">
                    Two columns: <code className="rounded bg-ink-100 px-1">student_id</code> and <code className="rounded bg-ink-100 px-1">degree_group_code</code>.
                    student_id can be the intern's student ID or their email address. Codes are listed on the Reference list tab.
                    Each intern gets their group's primary title. Nothing is saved until you confirm the preview.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-3">
                    <label className="btn-secondary inline-flex cursor-pointer items-center gap-1.5">
                        <ArrowUpTrayIcon className="h-4 w-4" /> Choose CSV file
                        <input type="file" accept=".csv,text/csv" className="sr-only" onChange={(e) => readFile(e.target.files[0])} />
                    </label>
                    {fileName && <span className="text-sm text-ink-700">{fileName}</span>}
                    <button type="button" className="text-sm font-medium text-brand-700 hover:underline" onClick={() => downloadBlob(new Blob([TEMPLATE], { type: 'text/csv' }), 'career-direction-template.csv')}>
                        Download template
                    </button>
                </div>
                <details className="mt-3">
                    <summary className="cursor-pointer text-xs text-ink-500">Or paste CSV text</summary>
                    <textarea rows={6} className={`${inputClass} mt-2 font-mono text-xs`} value={csv} onChange={(e) => { setCsv(e.target.value); setFileName(''); setPreview(null); }} placeholder={TEMPLATE} />
                </details>
                <div className="mt-4">
                    <button type="button" disabled={!csv.trim() || busy} onClick={runPreview} className="btn-primary disabled:opacity-50">
                        {busy === 'preview' ? 'Checking…' : 'Preview'}
                    </button>
                </div>
            </section>

            {preview && (
                <section className="space-y-4 rounded-2xl border border-border bg-white p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <p className="text-sm text-ink-800">
                            <span className="font-semibold">{preview.valid.length}</span> ready
                            ({preview.valid.filter((r) => r.changes).length} will change) ·{' '}
                            <span className={preview.failed.length ? 'font-semibold text-red-700' : ''}>{preview.failed.length} failed</span> of {preview.total} rows
                        </p>
                        {preview.committed ? (
                            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800">Applied {preview.committed.applied} rows</span>
                        ) : (
                            <button type="button" disabled={!preview.valid.length || busy} onClick={commit} className="btn-primary disabled:opacity-50">
                                {busy === 'commit' ? 'Applying…' : `Apply ${preview.valid.length} valid row${preview.valid.length === 1 ? '' : 's'}`}
                            </button>
                        )}
                    </div>

                    {preview.failed.length > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold text-red-700">Failed rows — these will be skipped</h3>
                            <PreviewTable
                                head={['Line', 'student_id', 'degree_group_code', 'Problem']}
                                rows={preview.failed.map((r) => [r.line, r.student_id, r.degree_group_code, r.reason])}
                            />
                        </div>
                    )}
                    {preview.valid.length > 0 && (
                        <div>
                            <h3 className="text-sm font-semibold text-ink-900">Ready to apply</h3>
                            <PreviewTable
                                head={['Line', 'Intern', 'Current group', 'New group', 'Career direction']}
                                rows={preview.valid.map((r) => [r.line, r.name, r.current_group || '—', r.degree_group_name, r.new_title])}
                            />
                        </div>
                    )}
                </section>
            )}
        </div>
    );
}

function PreviewTable({ head, rows }) {
    return (
        <div className="mt-2 max-h-80 overflow-auto rounded-xl border border-border">
            <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-ink-50 text-ink-600">
                    <tr>{head.map((h) => <th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {rows.map((r, i) => <tr key={i}>{r.map((c, j) => <td key={j} className="px-3 py-1.5 text-ink-800">{c}</td>)}</tr>)}
                </tbody>
            </table>
        </div>
    );
}

function ReferenceTab() {
    const [groups, setGroups] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState(null);

    useEffect(() => {
        careerDirectionApi.groups().then(setGroups).catch((err) => toast.error(errorMessage(err, 'Could not load the reference list')));
    }, []);

    if (!groups) return <div className="h-64 animate-pulse rounded-2xl bg-ink-100" />;

    const value = (g, field) => (drafts[g.code] && drafts[g.code][field] !== undefined ? drafts[g.code][field] : g[field] || '');
    const set = (code, field, v) => setDrafts((d) => ({ ...d, [code]: { ...(d[code] || {}), [field]: v } }));
    const dirty = (code) => Boolean(drafts[code] && Object.keys(drafts[code]).length);

    const save = async (g) => {
        setSaving(g.code);
        try {
            const updated = await careerDirectionApi.updateGroup(g.code, drafts[g.code]);
            setGroups((list) => list.map((x) => (x.code === g.code ? updated : x)));
            setDrafts((d) => { const next = { ...d }; delete next[g.code]; return next; });
            toast.success(`${updated.name} saved — interns on its primary title were updated`);
        } catch (err) {
            toast.error(errorMessage(err, 'Could not save'));
        } finally {
            setSaving(null);
        }
    };

    return (
        <div className="space-y-3">
            <p className="text-sm text-ink-600">
                Changing a primary title updates every intern who has it automatically; admin overrides are kept.
                Senior titles (Director, Head of, Chief, Architect, Lead) and clinical titles are rejected.
                “Match words” decide which degrees fall into each group — the longest matching word wins.
            </p>
            <div className="overflow-hidden rounded-2xl border border-border bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full min-w-[1100px] text-left text-sm">
                        <thead className="border-b border-border text-[11px] font-semibold uppercase tracking-wider text-ink-500">
                            <tr>
                                <th className="px-3 py-3">Code</th>
                                <th className="px-3 py-3">Degree program</th>
                                <th className="px-3 py-3">Primary title</th>
                                <th className="px-3 py-3">Alternate 1</th>
                                <th className="px-3 py-3">Alternate 2</th>
                                <th className="px-3 py-3">Match words</th>
                                <th className="px-3 py-3"><span className="sr-only">Save</span></th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {groups.map((g) => (
                                <tr key={g.code} className="align-top">
                                    <td className="px-3 py-2.5">
                                        <code className="text-xs text-ink-700">{g.code}</code>
                                        <p className="mt-0.5 text-[11px] text-ink-400">{g.category}</p>
                                    </td>
                                    <td className="px-3 py-2"><input aria-label={`${g.code} name`} className={inputClass} value={value(g, 'name')} onChange={(e) => set(g.code, 'name', e.target.value)} /></td>
                                    <td className="px-3 py-2"><input aria-label={`${g.code} primary title`} className={`${inputClass} font-semibold`} value={value(g, 'primary_title')} onChange={(e) => set(g.code, 'primary_title', e.target.value)} /></td>
                                    <td className="px-3 py-2"><input aria-label={`${g.code} alternate 1`} className={inputClass} value={value(g, 'alternate_1')} onChange={(e) => set(g.code, 'alternate_1', e.target.value)} /></td>
                                    <td className="px-3 py-2"><input aria-label={`${g.code} alternate 2`} className={inputClass} value={value(g, 'alternate_2')} onChange={(e) => set(g.code, 'alternate_2', e.target.value)} /></td>
                                    <td className="px-3 py-2">
                                        {g.code === 'other'
                                            ? <p className="pt-2 text-xs italic text-ink-400">Used when nothing else matches</p>
                                            : <textarea aria-label={`${g.code} match words`} rows={2} className={`${inputClass} text-xs`} value={value(g, 'match_keywords')} onChange={(e) => set(g.code, 'match_keywords', e.target.value)} />}
                                    </td>
                                    <td className="px-3 py-2">
                                        <button type="button" disabled={!dirty(g.code) || saving === g.code} onClick={() => save(g)} className="btn-primary disabled:opacity-40">
                                            {saving === g.code ? 'Saving…' : 'Save'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function SettingsTab() {
    const [settings, setSettings] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        careerDirectionApi.settings().then(setSettings).catch((err) => toast.error(errorMessage(err, 'Could not load settings')));
    }, []);

    if (!settings) return <div className="h-32 animate-pulse rounded-2xl bg-ink-100" />;

    const toggle = async () => {
        setSaving(true);
        try {
            setSettings(await careerDirectionApi.updateSettings({ show_on_certificate: !settings.show_on_certificate }));
            toast.success('Setting saved');
        } catch (err) {
            toast.error(errorMessage(err, 'Could not save the setting'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="max-w-2xl rounded-2xl border border-border bg-white p-5">
            <div className="flex items-start justify-between gap-6">
                <div>
                    <h2 className="text-[15px] font-semibold text-ink-950">Show career direction on completion certificates</h2>
                    <p className="mt-1 text-sm text-ink-600">
                        Off by default. When on, the intern's career direction appears on the certificate inside their portal.
                        It is never shown on the public certificate verification page.
                    </p>
                </div>
                <button
                    type="button" role="switch" aria-checked={settings.show_on_certificate} disabled={saving} onClick={toggle}
                    className={`relative mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${settings.show_on_certificate ? 'bg-brand-700' : 'bg-ink-200'}`}
                >
                    <span className="sr-only">Show career direction on certificates</span>
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${settings.show_on_certificate ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
            </div>
        </section>
    );
}
